import { Box, Typography, Button } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { useAtomValue } from 'jotai';
import {
  selectedReportFilters,
  selectedReportFields,
  allSitesRanDb,
} from '../../common/atom/atoms';
import { downloadExcelSheetFunction } from '../../common/functions/download_excel_sheet..function';
import React, { useState, useEffect } from 'react';
import DownloadReportButton from './download_report_button_ran_db.component';
import { getAllRanSites } from './functions/get_all_ran_sites.function';
import { getFieldDisplayName } from '../../common/functions/fieldNameMapping.function';
import { useRanDbDashboard } from './context/ranDbDashboardContext';

export default function DownloadReportCardRanDB() {
  const selectedFilters = useAtomValue(selectedReportFilters);
  const selectedFields = useAtomValue(selectedReportFields);
  const { dashboardData } = useRanDbDashboard();

  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);

  // Get applied filters count
  const appliedFiltersCount = Object.values(selectedFilters).filter(
    (f) => f && f.length > 0
  ).length;

  // Filter dashboard data based on selected filters
  useEffect(() => {
    const filterData = () => {
      setLoading(true);
      try {
        console.log('Filtering dashboard data with filters:', selectedFilters);
        console.log('Selected fields:', selectedFields);

        if (Array.isArray(dashboardData) && dashboardData.length > 0) {
          let filtered = [...dashboardData];
          console.log('Initial dashboard data count:', filtered.length);

          // Apply filters
          Object.entries(selectedFilters).forEach(([field, values]) => {
            if (values && values.length > 0) {
              console.log(`Applying filter for ${field}:`, values);
              filtered = filtered.filter((site) => {
                const siteValue = site[field];
                return values.some((value) => {
                  // Use exact match instead of partial match
                  return siteValue === value;
                });
              });
              console.log(`After filtering ${field}, count:`, filtered.length);
            }
          });

          // Filter data to include only selected fields
          const filteredDataWithSelectedFields = filtered.map((site) => {
            const filteredSite = {};
            selectedFields.forEach((field) => {
              if (site.hasOwnProperty(field)) {
                filteredSite[field] = site[field];
              }
            });
            return filteredSite;
          });

          console.log(
            'Final filtered data with selected fields only:',
            filteredDataWithSelectedFields
          );
          setFilteredData(filteredDataWithSelectedFields);
        } else {
          console.log('No dashboard data available for filtering');
          setFilteredData([]);
        }
      } catch (error) {
        console.error('Error filtering dashboard data:', error);
        setFilteredData([]);
      } finally {
        setLoading(false);
      }
    };

    filterData();
  }, [selectedFilters, selectedFields, dashboardData]);

  const handleDownload = () => {
    try {
      if (filteredData.length === 0) {
        alert(
          'No data found with the selected filters. Please check your filter criteria or try selecting different values.'
        );
        return;
      }

      const filename = `randb`;
      downloadExcelSheetFunction(filteredData, filename);
    } catch (error) {
      console.error('Download failed:', error);
      alert('Download failed. Please try again.');
    }
  };

  return (
    <Box
      sx={{
        width: '100%',
        flexGrow: 1,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        py: 4,
        gap: 3,
      }}
    >
      <Box
        sx={{
          width: 400,
          background: '#F8F8F8',
          borderRadius: 2,
          p: 3,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <Typography
          sx={{
            fontSize: 18,
            fontWeight: 600,
            color: '#1D242E',
            textAlign: 'center',
          }}
        >
          Report Summary
        </Typography>

        {loading ? (
          <Typography sx={{ fontSize: 14, color: '#666' }}>Generating report...</Typography>
        ) : (
          <>
            <Box sx={{ mt: 2, p: 2, backgroundColor: 'white', borderRadius: 1, width: '100%' }}>
              <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#1D242E', mb: 1 }}>
                Filter Fields ({selectedFields.length}):
              </Typography>
              <Typography sx={{ fontSize: 12, color: '#666', mb: 2 }}>
                {selectedFields.map((field) => getFieldDisplayName(field)).join(', ')}
              </Typography>
              <Typography sx={{ fontSize: 11, color: '#888', mb: 2, fontStyle: 'italic' }}>
                Note: Download will include ONLY the selected fields for the filtered sites
              </Typography>

              {appliedFiltersCount > 0 ? (
                <>
                  <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#1D242E', mb: 1 }}>
                    Applied Filters ({appliedFiltersCount}):
                  </Typography>
                  {Object.entries(selectedFilters)
                    .filter(([field, values]) => values && values.length > 0)
                    .map(([field, values]) => (
                      <Typography key={field} sx={{ fontSize: 12, color: '#666' }}>
                        • {field}: {Array.isArray(values) ? values.join(', ') : values}
                      </Typography>
                    ))}
                </>
              ) : (
                <Typography sx={{ fontSize: 12, color: '#666', mb: 2 }}>
                  No filters applied - all data will be included
                </Typography>
              )}

              <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#1D242E', mt: 2, mb: 1 }}>
                Results:
              </Typography>
              <Typography sx={{ fontSize: 12, color: '#666' }}>
                {filteredData.length} record(s) found
              </Typography>
            </Box>

            <DownloadReportButton onClick={handleDownload} disabled={loading} />
          </>
        )}
      </Box>
    </Box>
  );
}
